import { AudiencePageLayout, type AudienceContent } from '../audience/AudiencePageLayout';

const content: Record<'de' | 'en', AudienceContent> = {
  de: {
    title: 'qtplan – PERT-Schätzungen: Risiko wird eine Zahl | Dreipunktschätzung nach Clean Coder',
    metaDesc: 'PERT-Dreipunktschätzung nach Robert C. Martins Clean Coder. Drei Werte pro Task, Konfidenzintervalle statt Einzelwerte, individuelle Schätzungen pro Person.',
    hero: {
      h1: 'Drei Schätzwerte. Ein Konfidenzintervall. Kein Raten.',
      lead: 'Über den Worst Case nachzudenken ist entscheidend — genau das erzwingt die PERT-Methodik aus Robert C. Martins "The Clean Coder". Drei Werte pro Task: optimistisch, realistisch, pessimistisch. Daraus berechnet qtplan den Erwartungswert E = (O + 4M + P) / 6 und die Standardabweichung σ = (P - O) / 6. Das Ergebnis ist kein einzelner Wert sondern ein Konfidenzintervall.',
    },
    bento: {
      heading: 'Schätzungen die Unsicherheit sichtbar machen',
      items: [
        {
          span: 2,
          label: 'METHODE',
          title: 'Die Formel hinter dem Konfidenzintervall',
          body: 'PERT steht für Program Evaluation and Review Technique. Drei Schätzwerte — optimistisch (O), realistisch (M), pessimistisch (P) — werden zur gewichteten Schätzung E = (O + 4M + P) / 6 verrechnet. Die Standardabweichung σ = (P - O) / 6 zeigt die Unsicherheit. Das 95%-Konfidenzintervall liegt bei E ± 2σ. Keine Magie, reine Statistik — transparent und nachvollziehbar.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'INDIVIDUELL',
          title: 'Schätzungen pro Person',
          body: 'Derselbe Task dauert bei verschiedenen Leuten unterschiedlich lang. qtplan unterstützt individuelle Schätzungen pro Teammitglied. Wer den Task bekommt, beeinflusst die Timeline — und das ist sichtbar statt versteckt.',
        },
        {
          span: 1,
          label: 'BOARD',
          title: 'Schätzungen direkt auf dem Board erfassen',
          body: 'Tasks auf dem Board erhalten ihre PERT-Schätzungen direkt beim Erstellen. Kein separates Tool, kein Nachpflegen. Die Schätzung ist Teil des Tasks — nicht ein Kommentar oder eine Spalte in einer Tabelle.',
        },
        {
          span: 2,
          label: 'ROADMAP',
          title: 'Konfidenzintervalle in der Timeline',
          body: 'In der Gantt-Timeline werden Tasks nicht als feste Blöcke dargestellt sondern mit ihrem Konfidenzintervall. Ein Best-Case-Overlay (heller Bereich mit grünem Marker) zeigt den optimistischen Zeitrahmen, der volle Block den Erwartungswert. Wenn ein Task zwischen 3 und 8 Tagen dauern kann, sieht man das sofort — statt einen Mittelwert zu planen und sich zu wundern warum der Sprint länger dauert.',
        },
        {
          span: 1,
          label: 'RISIKO',
          title: 'Aggregiertes Risiko über alle Tasks',
          body: 'Die Übersicht aggregiert Einzelschätzungen zum Gesamtbild. Wenn viele Tasks hohe Unsicherheit haben, ist das Projektrisiko hoch — auch wenn jeder einzelne Task "machbar" aussieht. Das Gesamtbild zeigt was Einzelschätzungen verbergen.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'TEAM',
          title: 'Collaboration Penalty sichtbar machen',
          body: 'Mehr Leute bedeuten nicht automatisch schneller. qtplan berechnet eine Collaboration Penalty pro zusätzlichem Teammitglied auf demselben Task. Kommunikationsaufwand, Abstimmung, Kontextwechsel — der Effekt ist messbar und wird in die Timeline eingerechnet. Pro Person zeigt qtplan Contribution-Details: Schätzung, PERT-Wert, Risiko, Speed und prozentualen Anteil.',
        },
        {
          span: 1,
          label: 'SENSIBEL',
          title: 'Schätzungen schützen',
          body: 'Schätzungen sind keine Versprechen. qtplan läuft lokal im Browser — keine Cloud, kein Backend. Schätzungen verlassen nie den Rechner und können nicht gegen Teammitglieder verwendet werden. Das fördert ehrliche Schätzungen statt politisch motivierter.',
        },
        {
          span: 2,
          label: 'CLEAN CODER',
          title: 'Praxis nicht Theorie',
          body: 'Die PERT-Methodik ist nicht neu — aber sie wird selten konsequent angewendet weil die Tools fehlen. Robert C. Martin beschreibt sie in "The Clean Coder" als Standard für professionelle Softwareentwicklung. Das ist die Methode mit der man aufhört Deadlines zu raten. qtplan macht sie praktisch anwendbar: drei Felder pro Task, automatische Berechnung, sofort sichtbar in Board, Timeline und Übersicht.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Warum Schätzen ohne Methodik scheitert',
      paragraphs: [
        'Die meisten Teams schätzen mit Story Points, T-Shirt-Größen oder gar nicht. Das Ergebnis: Einzelwerte die Unsicherheit verbergen. Ein Task der "5 Tage" geschätzt wird, dauert vielleicht 3 oder 12 — aber der Plan zeigt nur die 5. Wenn dann drei solcher Tasks hintereinander liegen, ist der Sprint gesprengt.',
        'PERT löst dieses Problem indem es Unsicherheit explizit macht. Drei Schätzwerte erzwingen dass über den Best Case und den Worst Case nachgedacht wird. Der Erwartungswert ist realistischer als eine Einzelschätzung, und die Standardabweichung zeigt wo die großen Risiken liegen.',
        'In qtplan sind PERT-Schätzungen kein Add-on sondern das Fundament. Jeder Task bekommt drei Werte, daraus werden Konfidenzintervalle. Auf der Timeline zeigt ein Best-Case-Overlay den optimistischen Zeitrahmen, der volle Block den Erwartungswert. Man sieht nicht "dieser Sprint dauert 10 Tage" sondern "mit 85% Wahrscheinlichkeit zwischen 8 und 14 Tagen". Das ist ehrlich und schützt vor bösen Überraschungen.',
        'Dazu kommt die Möglichkeit individueller Schätzungen pro Person. Derselbe Task dauert bei einem Senior drei Tage, bei einem Junior acht. Wer den Task bekommt, beeinflusst die Timeline — und das sollte eine bewusste Entscheidung sein, keine Überraschung am Sprintende.',
      ],
    },
    cta: {
      heading: 'Erste Schätzungen eingeben und Konfidenzintervalle sehen',
      body: 'Kein Account. Keine Cloud. Alle Daten bleiben lokal im Browser. Board erstellen, Tasks mit drei Schätzwerten anlegen und erleben wie Unsicherheit sichtbar wird.',
    },
  },
  en: {
    title: 'qtplan – PERT Estimates: Risk Becomes a Number | Three-Point Estimation from Clean Coder',
    metaDesc: 'PERT three-point estimation from Robert C. Martin\'s Clean Coder. Three values per task, confidence intervals instead of single values, individual estimates per person.',
    hero: {
      h1: 'Three estimates. One confidence interval. No guessing.',
      lead: 'Thinking about the worst case is critical — that is exactly what the PERT methodology from Robert C. Martin\'s "The Clean Coder" enforces. Three values per task: optimistic, realistic, pessimistic. From these, qtplan calculates the expected value E = (O + 4M + P) / 6 and the standard deviation σ = (P - O) / 6. The result is not a single number but a confidence interval.',
    },
    bento: {
      heading: 'Estimates That Make Uncertainty Visible',
      items: [
        {
          span: 2,
          label: 'METHOD',
          title: 'The Formula Behind the Confidence Interval',
          body: 'PERT stands for Program Evaluation and Review Technique. Three estimates — optimistic (O), realistic (M), pessimistic (P) — are combined into the weighted estimate E = (O + 4M + P) / 6. The standard deviation σ = (P - O) / 6 shows the uncertainty. The 95% confidence interval is E ± 2σ. No magic, pure statistics — transparent and traceable.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'INDIVIDUAL',
          title: 'Estimates per Person',
          body: 'The same task takes different people different amounts of time. qtplan supports individual estimates per team member. Who gets the task affects the timeline — and that is visible rather than hidden.',
        },
        {
          span: 1,
          label: 'BOARD',
          title: 'Capture Estimates Directly on the Board',
          body: 'Tasks on the board receive their PERT estimates directly when created. No separate tool, no double maintenance. The estimate is part of the task — not a comment or a column in a spreadsheet.',
        },
        {
          span: 2,
          label: 'ROADMAP',
          title: 'Confidence Intervals in the Timeline',
          body: 'In the Gantt timeline, tasks are not displayed as fixed blocks but with their confidence interval. A best-case overlay (lighter area with green marker) shows the optimistic timeframe, the full block shows the expected value. When a task can take between 3 and 8 days, that is immediately apparent — instead of planning a midpoint and wondering why the sprint runs long.',
        },
        {
          span: 1,
          label: 'RISK',
          title: 'Aggregated Risk Across All Tasks',
          body: 'The overview aggregates individual estimates into the big picture. When many tasks have high uncertainty, the project risk is high — even if each individual task looks "doable." The big picture shows what individual estimates hide.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'TEAM',
          title: 'Make Collaboration Penalty Visible',
          body: 'More people does not automatically mean faster. qtplan calculates a collaboration penalty per additional team member on the same task. Communication overhead, coordination, context switching — the effect is measurable and factored into the timeline. Per person, qtplan shows contribution details: estimate, PERT value, risk, speed, and percentage share.',
        },
        {
          span: 1,
          label: 'SENSITIVE',
          title: 'Protect Estimates',
          body: 'Estimates are not promises. qtplan runs locally in the browser — no cloud, no backend. Estimates never leave the machine and cannot be used against team members. This encourages honest estimates instead of politically motivated ones.',
        },
        {
          span: 2,
          label: 'CLEAN CODER',
          title: 'Practice Not Theory',
          body: 'The PERT methodology is not new — but it is rarely applied consistently because the tools are missing. Robert C. Martin describes it in "The Clean Coder" as the standard for professional software development. This is the method that stops the guessing of deadlines. qtplan makes it practically applicable: three fields per task, automatic calculation, immediately visible in board, timeline, and overview.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Why Estimation Without Methodology Fails',
      paragraphs: [
        'Most teams estimate with story points, T-shirt sizes, or not at all. The result: single values that hide uncertainty. A task estimated at "5 days" might take 3 or 12 — but the plan only shows the 5. When three such tasks are lined up, the sprint is blown.',
        'PERT solves this problem by making uncertainty explicit. Three estimates force thinking about the best case and the worst case. The expected value is more realistic than a single estimate, and the standard deviation shows where the biggest risks lie.',
        'In qtplan, PERT estimates are not an add-on but the foundation. Every task gets three values, from which confidence intervals are calculated. On the timeline, a best-case overlay shows the optimistic timeframe, the full block shows the expected value. One does not see "this sprint takes 10 days" but "with 85% probability between 8 and 14 days." That is honest and protects against nasty surprises.',
        'Additionally, individual estimates per person are supported. The same task takes a senior three days, a junior eight. Who gets the task affects the timeline — and that should be a conscious decision, not a surprise at the end of the sprint.',
      ],
    },
    cta: {
      heading: 'Enter first estimates and see confidence intervals',
      body: 'No account. No cloud. All data stays local in the browser. Create a board, add tasks with three estimate values, and experience how uncertainty becomes visible.',
    },
  },
};

export function PertPage({ lang }: { lang: 'de' | 'en' }) {
  return <AudiencePageLayout lang={lang} slug="pert" content={content[lang]} basePath="features" />;
}
